import { Form } from 'antd';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { contractOptionsActions, getContractOptions, UnitType } from 'entities/Contract';
import { getInsuranceRateField, useGetInsuranceByWarehouseIdQuery } from 'entities/Insurance';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { getLoggedUserData, useAuthorizedState, useCheckUserDataComplete, UserAccountType } from 'entities/User';
import _ from 'lodash';
import { getBoxInfo, getWarehouseInfo } from 'pages/BookingPage';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInitContractInformationData } from '../utils/hooks/useInitContractInformationData';
import { GratitudeModalType, useOpenGratitudeModal } from 'features/GratitudeModal';
import { STARTED_BOOKING_DATA_KEY } from 'pages/BookingPage/utils/constants';
import { ModernContractDetails } from './ModernContractDetails';
import { ModernContractDetailsMobile } from './ModernContractDetailsMobile';
import { ModernSummaryList } from './ModernSummaryList';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface ModernContractInformationFormProps {
  selectedWarehouseId: string | undefined;
  onCompleteStepAuthorized: () => void;
  onCompleteStepUnAuthorized: () => void;
}

export const ModernContractInformationForm: FC<ModernContractInformationFormProps> = memo((props) => {
  const { onCompleteStepAuthorized, onCompleteStepUnAuthorized, selectedWarehouseId } = props;

  const { t } = useAppTranslation('booking');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isDesktop } = useScreenBreakpoints();

  const isAuthorized = useAuthorizedState();
  const boxInfo = useAppSelector(getBoxInfo);
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const loggedUserData = useAppSelector(getLoggedUserData);
  const isUserDataCompleted = useCheckUserDataComplete();
  const currencySymbol = useGetCurrencySymbol();
  const needAuthorization = !isAuthorized || !isUserDataCompleted;
  const contractOptions = useAppSelector(getContractOptions);
  const userAccountType = contractOptions.accountType;

  useInitContractInformationData();

  const { openGratitudeModal } = useOpenGratitudeModal();

  const { data: insurances } = useGetInsuranceByWarehouseIdQuery(selectedWarehouseId, { skip: !selectedWarehouseId });

  const insuranceOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    const rateField = getInsuranceRateField(contractOptions.invoiceFrequencyType);

    const filteredByRateFieldInsurance = insurances?.filter((insurance) => insurance[rateField]) || [];

    const options = filteredByRateFieldInsurance.map((insurance) => {
      const label = `${formatPrice(insurance[rateField])} ${currencySymbol}/${t(contractOptions.invoiceFrequencyType)}`;

      return {
        value: insurance.insuranceId,
        label,
      };
    });

    return options;
  }, [contractOptions.invoiceFrequencyType, insurances, currencySymbol, t]);

  const isValidForm = useMemo((): boolean => {
    let isValid = Boolean(contractOptions.contractDuration) && Boolean(contractOptions.startDate) && Boolean(contractOptions.rentOption);
    const hasInsurance = boxInfo?.unitType !== UnitType.OFFICE && !_.isEmpty(insurances) && !_.isEmpty(insuranceOptions);

    if (hasInsurance) {
      isValid = isValid && Boolean(contractOptions.insuranceId);
    }

    if (contractOptions.invoiceFrequencyType === InvoiceFrequencyType.DAY) {
      isValid = isValid && Boolean(contractOptions.endDate);
    }

    return isValid;
  }, [
    contractOptions.contractDuration,
    contractOptions.startDate,
    contractOptions.rentOption,
    contractOptions.invoiceFrequencyType,
    contractOptions.insuranceId,
    contractOptions.endDate,
    boxInfo?.unitType,
    insurances,
    insuranceOptions,
  ]);

  const goToNextStep = useCallback((): void => {
    needAuthorization ? onCompleteStepUnAuthorized() : onCompleteStepAuthorized();
  }, [needAuthorization, onCompleteStepAuthorized, onCompleteStepUnAuthorized]);

  const handleOpenGratitudeModal = useCallback((): void => {
    openGratitudeModal({
      description: t(
        'Our company values each and every customer. If you have any questions or feedback, please don`t hesitate to reach out!',
      ),
      type: GratitudeModalType.RESERVATION,
      buttonId: 'reservation id',
    });
  }, [openGratitudeModal, t]);

  const onReservationSuccess = useCallback(() => {
    dispatch(contractOptionsActions.resetContractOptions());

    localStorage.removeItem(STARTED_BOOKING_DATA_KEY);

    navigate(AppRoutes.MY_BOXES);

    handleOpenGratitudeModal();
  }, [dispatch, handleOpenGratitudeModal, navigate]);

  const [form] = Form.useForm();

  return (
    <Form className="space-y-6" name="contractInfo" form={form} autoComplete="off" onFinish={goToNextStep}>
      <div className="flex gap-6">
        <div className="flex-1">
          {isDesktop ? (
            <ModernContractDetails
              selectedWarehouse={warehouseInfo}
              boxInfo={boxInfo}
              userInfo={loggedUserData}
              userAccountType={userAccountType}
              selectedWarehouseId={selectedWarehouseId}
              isValidForm={isValidForm}
              onReservationSuccess={onReservationSuccess}
              goToNextStep={goToNextStep}
            />
          ) : (
            <ModernContractDetailsMobile
              selectedWarehouse={warehouseInfo}
              boxInfo={boxInfo}
              userInfo={loggedUserData}
              userAccountType={userAccountType || UserAccountType.PERSONAL}
              selectedWarehouseId={selectedWarehouseId}
              isValidForm={isValidForm}
              onReservationSuccess={onReservationSuccess}
              goToNextStep={goToNextStep}
            />
          )}
        </div>

        {isDesktop && (
          <div className="w-[350px] flex-shrink-0">
            <ModernSummaryList warehouseInfo={warehouseInfo} boxInfo={boxInfo} contractOptions={contractOptions} />
          </div>
        )}
      </div>
    </Form>
  );
});
